/**
 * The words and links for what the site's agent is doing (UX review round 11),
 * shared by the header's "Agent working" popover and Today's line
 * (AgentStatus.tsx). Kept apart from the components so tests can load it.
 */

export type AgentWork = {
  agentName?: string | null;
  doing: string;
  startedAt: string;
  kind?: string | null;
  subjectId?: string | null;
  subjectTitle?: string | null;
};

/** "18 min ago", "2 hours ago", "3 days ago". */
export const ago = (ms: number) => {
  const min = Math.round(ms / 60000);
  if (min < 60) return `${min} min ago`;
  const h = Math.round(min / 60);
  if (h < 24) return `${h} hour${h === 1 ? '' : 's'} ago`;
  const d = Math.round(h / 24);
  return `${d} day${d === 1 ? '' : 's'} ago`;
};

const named = (t: string, what: string) =>
  new RegExp(`\\b${what}$`, 'i').test(t) ? t : `${t} ${what}`;

/**
 * Where the agent's item opens in the app, and the link's words ("Open the
 * About page"). Null when the log doesn't know the item.
 */
export function agentWorkLink(w: AgentWork): { to: string; label: string } | null {
  const id = w.subjectId;
  const title = w.subjectTitle?.trim();
  if (w.kind === 'social') return { to: '/reach', label: 'Open Social' };
  if (!id) return null;
  switch (w.kind) {
    case 'page':
      return {
        to: `/content?view=pages&page=${id}`,
        label: title ? `Open the ${named(title, 'page')}` : 'Open the page',
      };
    case 'form':
      return {
        to: `/content?view=forms&form=${id}`,
        label: title ? `Open the ${named(title, 'form')}` : 'Open the form',
      };
    case 'article':
      return { to: `/content/${id}`, label: 'Open the article' };
    case 'campaign':
      return { to: `/campaigns?campaign=${id}`, label: 'Open the campaign' };
    default:
      return null;
  }
}

/** "9:42 AM" and "18 min ago" for when the run started. */
export function startedWords(startedAt: string, now = Date.now()) {
  const at = new Date(startedAt);
  if (Number.isNaN(at.getTime())) return { clock: '', ago: '' };
  const ms = Math.max(0, now - at.getTime());
  return {
    clock: at.toLocaleTimeString([], { hour: 'numeric', minute: '2-digit' }),
    ago: ms < 60000 ? 'just now' : ago(ms),
  };
}

/**
 * The popover's words as Today's sentence: "Drafting an update to your About
 * page" → "working on an update to your About page"; "Writing a new article,
 * “X”" → "writing a new article, “X”".
 */
export function agentRowWords(doing: string): string {
  const m = /^Drafting (an update to|a new) (.*)$/.exec(doing);
  if (m) return `working on ${m[1]} ${m[2]}`;
  return doing ? doing[0].toLowerCase() + doing.slice(1) : 'working';
}
